import axios from 'axios'
import config from '@/config.js'
import { getStatic, readOnlyError } from '@/services/data-source.js'
import {
  normalizeDocuments,
  normalizeLookups,
  normalizeTokenMetadata,
  searchStaticTokenIndex,
} from '@/services/token-search.js'

const API_URL = config.apiUrl
let tokenLocations = null
let piDataRequest = null

const state = {
  types: [],
  allTypes: [],
  allTypesDocs: {},
  allLemmaTypes: {},
  allDocuments: {},
  tokenMetadata: {},
  lookups: {heads: [], corpora: [], lemmata: {}, hccs: {}},
  context: [],
  tokensSettings: null,
  documentsPerCorpus: {},
  selectedTokensLemmas: [],
  PIDataLoaded: false,
  PIDataLoading: false,
  PIDataError: null,
  PIDataWarning: null,
}

const mutations = {
  setTypes(state, types) {
    state.types = types;
  },
  setAllTypes(state, types) {
    state.allTypes = Object.freeze(types);
  },
  setTypesDocs(state, data) {
    state.allTypesDocs = Object.freeze(data);
  },
  setLemmaTypes(state, data) {
    state.allLemmaTypes = Object.freeze(data);
  },
  setAllDocuments(state, data) {
    const normalized = normalizeDocuments(data)
    let documentsPerCorpus = {}
    tokenLocations = null
    Object.keys(normalized).forEach(documentId => {
      const document = normalized[documentId]
      if (!documentsPerCorpus[document.corpus]) {
        documentsPerCorpus[document.corpus] = []
      }
      documentsPerCorpus[document.corpus].push(documentId)
    })
    state.documentsPerCorpus = documentsPerCorpus;
    state.allDocuments = Object.freeze(normalized);
  },
  setTokenMetadata(state, data) {
    state.tokenMetadata = Object.freeze(normalizeTokenMetadata(data))
  },
  setTokenLookups(state, data) {
    state.lookups = normalizeLookups(data)
  },
  setPIDataLoading(state, loading) {
    state.PIDataLoading = loading
  },
  setPIDataError(state, error) {
    state.PIDataError = error
  },
  setPIDataWarning(state, warning) {
    state.PIDataWarning = warning
  },
  setPIDataLoaded(state, loaded) {
    state.PIDataLoaded = loaded
  },
  setContext(state, context) {
    state.context = context;
  },
}

const getters = {
}

const actions = {
  loadPIData({ commit, dispatch, state }) {
    if (state.PIDataLoaded) {
      return Promise.resolve()
    }
    if (piDataRequest) {
      return piDataRequest
    }
    commit('setPIDataLoading', true)
    commit('setPIDataError', null)
    commit('setPIDataWarning', null)
    piDataRequest = (async () => {
      try {
      const [types, typesDocs, lemmaTypes, documents, tokenMetadata, lookups] = await Promise.all([
        getStatic('token_search/types.json'),
        getStatic('token_search/types_docs_idxs.json'),
        getStatic('token_search/lemma_types.json'),
        getStatic('token_search/documents.json'),
        getStatic('token_search/token_metadata.json').catch(error => {
          if (error.response && error.response.status === 404) return {data: {}, missing: true}
          throw error
        }),
        getStatic('token_search/lookups.json').catch(error => {
          if (error.response && error.response.status === 404) return {data: null, missing: true}
          throw error
        }),
      ])
      commit('setAllTypes', types.data)
      commit('setTypesDocs', typesDocs.data)
      commit('setLemmaTypes', lemmaTypes.data)
      commit('setAllDocuments', documents.data)
      commit('setTokenMetadata', tokenMetadata.data)
      commit('setTokenLookups', lookups.data)
      const hasEmbeddedMetadata = Object.values(state.allDocuments).some(
        document => document.hasMetadata
      )
      if ((!hasEmbeddedMetadata && tokenMetadata.missing) || lookups.missing) {
        commit(
          'setPIDataWarning',
          'This is a legacy DocLing export. Head filtering and resolved lemma/HCC context require a regenerated export.'
        )
      }
      commit('setPIDataLoaded', true)
      } catch (error) {
        commit('setPIDataLoaded', false)
        commit('setPIDataError', error.message || 'Static DocLing search indexes are unavailable')
        dispatch('addNotification', {
          type: 'danger',
          text: 'Static DocLing search indexes are unavailable. Regenerate the JSON export.'
        })
        throw error
      } finally {
        commit('setPIDataLoading', false)
        piDataRequest = null
      }
    })()
    return piDataRequest
  },
  async searchTypes({ commit, dispatch, state }, params) {
    if (config.staticData) {
      await dispatch('loadPIData')
      try {
        const results = searchStaticTokenIndex({
          types: state.allTypes,
          typesDocs: state.allTypesDocs,
          lemmaTypes: state.allLemmaTypes,
          documents: state.allDocuments,
          tokenMetadata: state.tokenMetadata,
        }, params)
        commit('setTypes', results)
        return results
      } catch (error) {
        commit('setTypes', [])
        dispatch('addNotification', {
          type: 'danger',
          text: error.message || 'The DocLing search could not be completed',
        })
        throw error
      }
    }
    return axios
        .post(`${API_URL}/token/types`, {
          queryRegex: params.queryRegex,
          caseSensitive: params.caseSensitive,
          searchBy: params.searchBy,
          documents: params.documents,
          scriptoria: params.scriptoria,
          corpuses: params.corpora,
          languages: params.languages,
          lemmatized: params.lemmatized,
          headId: params.headId,
          excludeLatin: params.excludeLatin
        })
        .then(r => r.data)
        .then(types => {
          commit('setTypes', types);
          return types
        })
  },
  loadContext({ commit, state }, tokenIds) {
    if (config.staticData) {
      if (!tokenLocations) {
        tokenLocations = new Map()
        Object.entries(state.allDocuments).forEach(([documentId, document]) => {
          document.tokenIds.forEach((tokenId, index) => {
            tokenLocations.set(String(tokenId), {documentId, index})
          })
        })
      }
      const selectedTokenIds = new Set(tokenIds.map(String))
      const results = {
        documents: {},
        results: [],
        lemmata: state.lookups.lemmata,
        hccs: state.lookups.hccs,
      }
      selectedTokenIds.forEach(tokenId => {
        const location = tokenLocations.get(tokenId)
        if (!location) {
          return
        }
        const document = state.allDocuments[location.documentId]
        const metadata = state.tokenMetadata[tokenId] || {
          languages: document.languages[location.index] || [],
          lemmaIds: (document.lemmaIds[location.index] || []).map(String),
          hccIds: (document.hccIds[location.index] || []).map(String),
        }
        results.documents[location.documentId] = {
          date: document.date,
          sigle_legam: document.editionSigle,
          scriptorium: document.scriptoria.join(', '),
          scripta: document.scripta.join(', '),
        }
        const left = document.typeIndices.slice(Math.max(0, location.index - 50), location.index)
        const right = document.typeIndices.slice(location.index + 1, location.index + 51)
        results.results.push({
          document_id: location.documentId,
          token_id: tokenId,
          form: state.allTypes[document.typeIndices[location.index]] || '',
          left: left.map(typeIndex => state.allTypes[typeIndex] || '').join(' '),
          right: right.map(typeIndex => state.allTypes[typeIndex] || '').join(' '),
          division: document.divisions[location.index] === undefined
            ? null
            : document.divisions[location.index],
          lemma_id: metadata.lemmaIds,
          hcc_id: metadata.hccIds,
          lang: metadata.languages,
          last_token_left: location.index,
        })
      })
      commit('setContext', results)
      return Promise.resolve(results)
    }
    return axios
      .post(`${API_URL}/token/load-context`, {
        tokens: tokenIds,
      })
      .then(r => r.data)
      .then(context => {
        commit('setContext', context);
        return context
      })
  },
  assignTokensToLemma(_, data) {
    if (!config.editable) {
      return readOnlyError('Assigning tokens to a lemma')
    }
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/${data.lemmaId}/assign_tokens`, { tokenIds: data.tokenIds })
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
  removeAssignedLemmas(_, data) {
    if (!config.editable) {
      return readOnlyError('Removing assigned lemmata')
    }
    return new Promise((resolve) => {
      axios
        .post(`${API_URL}/lemma/unassign_tokens`, { tokenIds: data.tokenIds , lemmaIds: data.lemmaIds})
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          this.dispatch('addNotification', {
            type: 'danger',
            text: `Something went wrong`
          })
        })
    })
  },
}

export default {
    state,
    getters,
    actions,
    mutations
}
